/**
 * PlanGate - blocks access to features that require a higher plan tier.
 * Wraps page content and shows an upgrade wall if the user's plan is too low.
 */

import { Lock, Sparkles, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuthStore } from '@/store/authStore';
import { useNavigate } from 'react-router-dom';

const PLAN_NAMES: Record<number, string> = {
  0: 'Free Trial',
  1: 'Starter',
  2: 'Professional',
  3: 'Enterprise',
  4: 'Scale',
};

interface PlanGateProps {
  minPlan: number; // 0=trial, 1=starter, 2=pro, 3=enterprise, 4=scale
  featureName: string;
  children: React.ReactNode;
}

export function PlanGate({ minPlan, featureName, children }: PlanGateProps) {
  const { hasPlanTier, tenantPlan } = useAuthStore();
  const navigate = useNavigate();

  if (hasPlanTier(minPlan)) {
    return <>{children}</>;
  }

  const requiredPlanName = PLAN_NAMES[minPlan] || 'a higher plan';

  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="text-center max-w-md mx-auto px-6">
        <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
          <Lock className="h-7 w-7 text-primary" />
        </div>
        <h2 className="text-xl font-semibold mb-2">{featureName}</h2>
        <p className="text-sm text-muted-foreground mb-6">
          This feature requires the <strong>{requiredPlanName}</strong> plan or higher.
          You're currently on the <strong>{PLAN_NAMES[({ trial: 0, starter: 1, professional: 2, enterprise: 3, unlimited: 4 } as Record<string, number>)[tenantPlan] ?? 0] || tenantPlan}</strong> plan.
        </p>
        <Button onClick={() => navigate('/panel/upgrade')} size="lg">
          <Sparkles className="h-4 w-4 mr-2" />
          Upgrade to {requiredPlanName}
          <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
        <p className="text-xs text-muted-foreground mt-4">
          Upgrade takes effect immediately. No downtime.
        </p>
      </div>
    </div>
  );
}
